import { api, apiErrorMessage, oneSpec, useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    KeyValueList,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { Receipt, StaffOrder } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { RefundSheet } from '@/components/refund-sheet';
import {
    useChargeSubjects,
    WebhookDeliveries,
    type ChargeSubjects,
} from '@/components/webhook-deliveries';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { Issuer } from '@/pages/money/refunds';

/** The reads of `/orders/:id` (BM-46 §2.0). */
export const orderReads = (id: string | undefined) => ({
    order: oneSpec(`/staff/orders/${id ?? ''}`),
});

/** A titled card of a detail page (the order and subscription pages). */
export function Section({
    title,
    testId,
    children,
}: {
    title: string;
    testId: string;
    children: ReactNode;
}) {
    return (
        <Card data-testid={testId}>
            <CardHeader>
                <CardTitle className="text-base">{title}</CardTitle>
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

/** bm-147 § 3 (D-3): which subscription and period the order paid for, and whether it is the sign-up's or a renewal's. */
function SubscriptionBlock({ order: o }: { order: StaffOrder }) {
    if (!o.subscription_id) return null;
    return (
        <p className="text-sm" data-testid="order-subscription">
            Subscription{' '}
            <Link
                to={`/subscriptions/${encodeURIComponent(o.subscription_id)}`}
                className="font-mono text-xs hover:underline"
            >
                {o.subscription_id}
            </Link>
            {o.period
                ? ` · period ${o.period.number ?? '—'} · ${o.period.kind === 'origin' ? 'origin order' : 'renewal'}`
                : null}
        </p>
    );
}

function OrderBody({
    order: o,
    subjects,
}: {
    order: StaffOrder;
    subjects: ChargeSubjects;
}) {
    const money = (cents: number | null | undefined, empty = '—') =>
        cents == null ? empty : <Money cents={cents} currency={o.currency} />;
    return (
        <div className="space-y-4">
            <SubscriptionBlock order={o} />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Section title="Money" testId="order-money">
                    <KeyValueList
                        items={[
                            {
                                label: 'Status',
                                value: (
                                    <StatusBadge
                                        status={o.status}
                                        domain="order"
                                    />
                                ),
                            },
                            {
                                label: 'Subtotal',
                                value: money(o.subtotal_cents),
                                num: true,
                            },
                            {
                                label: 'Tax',
                                value: (
                                    <span className="inline-flex items-center gap-2">
                                        {money(o.tax_cents)}
                                        {o.tax_fallback ? (
                                            <Badge
                                                variant="outline"
                                                data-testid="tax-fallback"
                                            >
                                                Fallback tax rate
                                            </Badge>
                                        ) : null}
                                    </span>
                                ),
                                num: true,
                            },
                            {
                                label: 'Total',
                                value: money(o.total_cents),
                                num: true,
                            },
                            {
                                label: 'Refunded',
                                value: money(o.refunded_cents ?? 0),
                                num: true,
                            },
                            {
                                label: 'Refundable',
                                value: money(o.refundable_cents),
                                num: true,
                            },
                            {
                                label: 'Captured',
                                value: <DateTime value={o.captured_at} />,
                            },
                            {
                                label: 'Available for payout',
                                value: (
                                    <DateTime
                                        value={o.available_at}
                                        empty="Not settled yet"
                                    />
                                ),
                            },
                        ]}
                    />
                </Section>
                <Section title="Staff pricing" testId="order-pricing">
                    <KeyValueList
                        items={[
                            {
                                label: 'Fee rule',
                                value: o.fee_rule ?? '—',
                            },
                            o.fee_card
                                ? {
                                      label: 'Fee card',
                                      value: o.fee_card.id ? (
                                          <PublicId id={o.fee_card.id} />
                                      ) : (
                                          '—'
                                      ),
                                  }
                                : null,
                            o.fee_card
                                ? {
                                      label: 'Fee card version',
                                      value: o.fee_card.version,
                                      num: true,
                                  }
                                : null,
                            {
                                label: 'Fee',
                                value: money(o.fee_cents),
                                num: true,
                            },
                            ...(o.fee_breakdown ?? []).map((r, i) => ({
                                key: `fee-${i}`,
                                label: `${r.name ?? r.kind} (${r.pct_bps} bps)`,
                                value: money(r.cents),
                                num: true,
                            })),
                            {
                                label: 'Expected cost',
                                value: money(o.expected_cost_cents),
                                num: true,
                            },
                            ...o.cost_breakdown.map((c) => ({
                                key: `cost-${c.cost_line}`,
                                label: c.cost_line,
                                value: money(c.cents),
                                num: true,
                            })),
                            {
                                label: 'Expected margin',
                                value: money(o.expected_margin_cents),
                                num: true,
                            },
                            {
                                label: 'Actual margin',
                                value: money(
                                    o.actual_margin_cents,
                                    'Not settled yet',
                                ),
                                num: true,
                            },
                            {
                                label: 'Net to merchant',
                                value: money(
                                    o.net_to_merchant_cents,
                                    'Not posted yet',
                                ),
                                num: true,
                            },
                            {
                                label: 'Reserve',
                                value: money(o.reserve_cents ?? 0),
                                num: true,
                            },
                            {
                                label: 'Reserve release',
                                value: (
                                    <DateTime
                                        value={o.reserve_release_at}
                                        empty="—"
                                    />
                                ),
                            },
                        ]}
                    />
                </Section>
                <Section title="Links" testId="order-links">
                    <KeyValueList
                        items={[
                            {
                                label: 'Charge',
                                value: <ObjectRef id={o.charge_id} />,
                            },
                            o.session_id
                                ? {
                                      label: 'Session',
                                      value: <ObjectRef id={o.session_id} />,
                                  }
                                : null,
                            o.period
                                ? {
                                      label: 'Period',
                                      value: <PublicId id={o.period.id} />,
                                  }
                                : null,
                            {
                                label: 'Merchant',
                                value: <ObjectRef id={o.merchant_id} />,
                            },
                            o.store_id
                                ? {
                                      label: 'Store',
                                      value: (
                                          <ObjectRef
                                              id={o.store_id}
                                              label={
                                                  o.store?.display_name ??
                                                  o.store?.name
                                              }
                                              href={`/stores/${encodeURIComponent(o.store_id)}`}
                                          />
                                      ),
                                  }
                                : null,
                            {
                                label: 'Customer',
                                // no admin customer page in this ticket (BM-133): the plain id with a copy button
                                value: o.customer_id ? (
                                    <span data-testid="order-customer">
                                        <PublicId
                                            id={o.customer_id}
                                            short={false}
                                        />
                                    </span>
                                ) : (
                                    '—'
                                ),
                            },
                            { label: 'Buyer', value: o.buyer_email },
                        ]}
                    />
                </Section>
                <Section
                    title="Fulfilment and access"
                    testId="order-fulfilment"
                >
                    <KeyValueList
                        items={[
                            {
                                label: 'Fulfilment due',
                                value: (
                                    <DateTime
                                        value={o.fulfilment_due_at}
                                        empty="—"
                                    />
                                ),
                            },
                            {
                                label: 'Fulfilled',
                                value: (
                                    <DateTime
                                        value={o.fulfilled_at}
                                        empty="Not yet"
                                    />
                                ),
                            },
                            o.fulfilment_evidence
                                ? {
                                      label: 'Evidence',
                                      value: JSON.stringify(
                                          o.fulfilment_evidence,
                                      ),
                                      mono: true,
                                  }
                                : null,
                            {
                                label: 'Access revoked',
                                value: o.access_revoked_at ? (
                                    <DateTime value={o.access_revoked_at} />
                                ) : (
                                    'No'
                                ),
                            },
                            o.access_revoked_reason
                                ? {
                                      label: 'Revoke reason',
                                      value: o.access_revoked_reason,
                                  }
                                : null,
                        ]}
                    />
                </Section>
            </div>
            <Section title="Lines" testId="order-lines">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Item</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>Unit price</TableHead>
                                <TableHead>Tax</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {o.lines.map((l, i) => (
                                <TableRow key={`${l.price_id ?? ''}-${i}`}>
                                    <TableCell>{l.name ?? '—'}</TableCell>
                                    <TableCell className="tabular-nums">
                                        {l.quantity ?? '—'}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {money(l.unit_price_cents)}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {money(l.tax_cents)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </Section>
            <Section title="Refunds" testId="order-refunds">
                {o.refunds.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No refund was requested for this order yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Refund</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead>Initiated by</TableHead>
                                    <TableHead>Approved by</TableHead>
                                    <TableHead>Created</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {o.refunds.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell>
                                            <PublicId id={r.id} />
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {money(r.amount_cents)}
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                status={r.state}
                                                domain="refund"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Issuer refund={r} />
                                        </TableCell>
                                        <TableCell>
                                            {r.decided_by === null
                                                ? '—'
                                                : (r.decided_by_name ??
                                                  r.decided_by)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <DateTime value={r.created_at} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Section>
            <WebhookDeliveries {...subjects} />
        </div>
    );
}

/**
 * bm-147 § 3 (US-003): one order as staff read it (§ 2's staff order shape): money, staff pricing, links, the
 * subscription and period it paid for, lines and tax, fulfilment and access, refunds and the deliveries panel. Refund
 * opens the charge page's sheet bound to the order's charge; Resend receipt reissues the receipt link; Open in Ledger
 * opens the Ledger board only (D-17), with the order id and a copy button beside it.
 */
export function OrderPage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const query = useOne<StaffOrder>(orderReads(id).order.path);
    const [refunding, setRefunding] = useState(false);
    const receipt = useWrite(async () =>
        api().get<Receipt>(`/staff/orders/${encodeURIComponent(id)}/receipt`),
    );
    const o = query.data?.data;
    // the charge page's rule (canRefund): the order's refundable_cents is its charge's, 0 when the charge is not
    // captured (voided, failed, a free-trial hold), so the captured check rides in it (bm-147 D-3)
    const refundable =
        o !== undefined && can(P.refundsIssue) && o.refundable_cents > 0;
    // bm-147 § 8 (K-2): the deliveries of the order, its charge, the charge's refunds and disputes
    const subjects = useChargeSubjects(
        o && { orderId: o.id, chargeId: o.charge_id, refunds: o.refunds },
    );
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Orders', href: '/orders' }]}
                {...detailTitle(query, null, id, 'Order')}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    refundable ? (
                        <Button onClick={() => setRefunding(true)}>
                            Refund
                        </Button>
                    ) : null
                }
                secondaryAction={
                    o && can(P.ordersReissue) ? (
                        <Button
                            variant="outline"
                            disabled={receipt.isPending}
                            onClick={() => receipt.mutate(undefined)}
                        >
                            Resend receipt
                        </Button>
                    ) : null
                }
            />
            <div
                className="mb-4 flex flex-wrap items-center gap-2 text-sm"
                data-testid="order-ledger"
            >
                <Button variant="outline" size="sm" asChild>
                    <a href="/ledger" target="_blank" rel="noreferrer">
                        Open in Ledger
                    </a>
                </Button>
                <span className="text-muted-foreground">
                    Search the board for
                </span>
                <PublicId id={id} short={false} />
            </div>
            {receipt.data?.receipt_url ? (
                <p className="mb-3 text-sm">
                    Receipt:{' '}
                    <a
                        href={receipt.data.receipt_url}
                        className="break-all underline"
                        target="_blank"
                        rel="noreferrer"
                    >
                        {receipt.data.receipt_url}
                    </a>
                </p>
            ) : null}
            {receipt.error ? (
                <p role="alert" className="text-destructive mb-3 text-sm">
                    {apiErrorMessage(receipt.error)}
                </p>
            ) : null}
            <DetailBody query={query} title="Order">
                {(order) => <OrderBody order={order} subjects={subjects} />}
            </DetailBody>
            {refunding && o ? (
                <RefundSheet
                    bound={{
                        id: o.charge_id,
                        refundable_cents: o.refundable_cents,
                    }}
                    onClose={() => setRefunding(false)}
                />
            ) : null}
        </div>
    );
}
